import React from "react";
import { Link } from "react-router-dom";
import {
  FileText, MessageSquare, History, UserPlus, Clock, Mail,
  CheckCircle2, XCircle, Flag, DollarSign,
} from "lucide-react";
import { STAGE_LABEL, SPAM_REASON_LABEL, formatRelative, formatMoney, LEAD_TYPE_LABEL } from "../utils";

const ICON_BY_ACTION = {
  stage_moved:          History,
  assigned:             UserPlus,
  update:               History,
  follow_up_set:        Clock,
  follow_up_completed:  CheckCircle2,
  follow_up_cancelled:  XCircle,
  resend_email:         Mail,
  soft_delete:          XCircle,
  restore:              CheckCircle2,
  value_set:            DollarSign,
};

const TINT_BY_ACTION = {
  stage_moved:          "bg-blue-50 text-blue-700 border-blue-200",
  assigned:             "bg-violet-50 text-violet-700 border-violet-200",
  follow_up_set:        "bg-amber-50 text-amber-700 border-amber-200",
  follow_up_completed:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  follow_up_cancelled:  "bg-slate-100 text-slate-700 border-slate-200",
  resend_email:         "bg-sky-50 text-sky-700 border-sky-200",
  value_set:            "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const describePayload = (action, payload) => {
  if (!payload || Object.keys(payload).length === 0) return null;
  switch (action) {
    case "stage_moved": {
      const from = STAGE_LABEL[payload.from] || payload.from || "—";
      const to = STAGE_LABEL[payload.to] || payload.to || "—";
      const extras = [];
      if (payload.spam_reason) {
        extras.push(`reason: ${SPAM_REASON_LABEL[payload.spam_reason] || payload.spam_reason}`);
      }
      if (payload.spam_reason_note) extras.push(`note: ${payload.spam_reason_note}`);
      if (payload.final_won_value) extras.push(`value: ${formatMoney(payload.final_won_value)}`);
      return `${from} → ${to}${extras.length ? ` • ${extras.join(" • ")}` : ""}`;
    }
    case "assigned": {
      return payload.name ? `Assigned to ${payload.name}` : "Assignment cleared";
    }
    case "follow_up_set": {
      const preset = payload.preset ? `${payload.preset}` : "";
      return `Reminder set${preset ? ` (${preset})` : ""}${payload.due ? ` — due ${new Date(payload.due).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}`;
    }
    case "follow_up_completed": return "Follow-up marked complete";
    case "follow_up_cancelled": return `Follow-up cancelled${payload.reason ? ` (${payload.reason})` : ""}`;
    case "value_set":           return `Estimated value set to ${formatMoney(payload.value)}`;
    case "update": {
      const bits = [];
      if (payload.lead_type) bits.push(`Type → ${LEAD_TYPE_LABEL[payload.lead_type] || payload.lead_type}`);
      if (payload.final_won_value) bits.push(`Won value → ${formatMoney(payload.final_won_value)}`);
      if (payload.score !== undefined) bits.push(`Score → ${payload.score}`);
      if (payload.assigned_to_name) bits.push(`Assigned → ${payload.assigned_to_name}`);
      return bits.length ? bits.join(" • ") : null;
    }
    default:
      return Object.entries(payload).map(([k, v]) => `${k}: ${v}`).join(" • ");
  }
};

const labelForAction = (a) => {
  const map = {
    stage_moved: "Stage changed",
    assigned: "Assignment updated",
    follow_up_set: "Follow-up set",
    follow_up_completed: "Follow-up completed",
    follow_up_cancelled: "Follow-up cancelled",
    resend_email: "Email re-sent",
    soft_delete: "Soft-deleted",
    restore: "Restored",
    value_set: "Pipeline value updated",
    update: "Lead updated",
  };
  return map[a] || (a || "").replace(/_/g, " ");
};

/**
 * Unified Activity Timeline.
 * Merges the lead's audit_log entries with manual notes into a single
 * chronological feed (newest first). Each event is rendered as an icon
 * dot + a one-line summary + an optional payload description.
 *
 * Props:
 *   doc — submission with audit_log[] and notes[]
 */
export default function ActivityTimeline({ doc }) {
  const auditEvents = (doc.audit_log || [])
    .filter((e) => e && typeof e === "object")
    .map((e, idx) => ({
      kind: "audit",
      id: `${e.ts || ""}-${e.action || ""}-${idx}`,
      ts: e.ts,
      actor: e.actor_name || e.actor_email || "system",
      action: e.action,
      payload: e.payload,
    }));

  const noteEvents = (Array.isArray(doc.notes) ? doc.notes : [])
    .filter((n) => n && typeof n === "object")
    .map((n) => ({
      kind: "note",
      id: n.id || `note-${n.ts}`,
      ts: n.ts,
      actor: n.actor_name || n.actor_email || "user",
      text: n.text,
    }));

  // Synthetic "Lead created" event from created_at
  const createdEvent = doc.created_at && {
    kind: "audit",
    id: "lead-created",
    ts: doc.created_at,
    actor: "system",
    action: "lead_created",
    payload: { formType: doc.formType },
  };

  const all = [
    ...(createdEvent ? [createdEvent] : []),
    ...auditEvents,
    ...noteEvents,
  ]
    .filter((e) => e.ts)
    .sort((a, b) => new Date(b.ts) - new Date(a.ts));

  if (all.length === 0) {
    return <p className="text-sm text-slate-500">No activity yet.</p>;
  }

  return (
    <ol className="relative space-y-3" data-testid="activity-timeline">
      <div aria-hidden className="absolute left-3 top-1 bottom-1 w-px bg-slate-200" />
      {all.map((e) => {
        if (e.kind === "note") {
          return (
            <li key={e.id} className="relative pl-9" data-testid="timeline-note">
              <div className="absolute left-0 top-0.5 flex items-center justify-center w-7 h-7 rounded-full bg-white border border-slate-200">
                <MessageSquare className="h-3.5 w-3.5 text-slate-500" />
              </div>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-700">{e.actor}</span>
                <span className="text-[11px] text-slate-500">{formatRelative(e.ts)}</span>
              </div>
              <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap leading-snug">{e.text}</p>
            </li>
          );
        }
        const Icon = e.action === "lead_created" ? FileText : (ICON_BY_ACTION[e.action] || History);
        const tint = e.action === "lead_created"
          ? "bg-slate-100 text-slate-700 border-slate-200"
          : (TINT_BY_ACTION[e.action] || "bg-slate-100 text-slate-700 border-slate-200");
        const description = e.action === "lead_created"
          ? `New submission via ${(e.payload?.formType || "form").replace(/_/g, " ")}`
          : describePayload(e.action, e.payload);
        return (
          <li key={e.id} className="relative pl-9" data-testid="timeline-audit">
            <div className={`absolute left-0 top-0.5 flex items-center justify-center w-7 h-7 rounded-full border ${tint}`}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <span className="text-xs font-medium text-slate-700">
                {labelForAction(e.action)}
                <span className="text-slate-400 font-normal"> • {e.actor}</span>
              </span>
              <span className="text-[11px] text-slate-500">{formatRelative(e.ts)}</span>
            </div>
            {description && (
              <div className="text-sm text-slate-700 mt-0.5 leading-snug">{description}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
